import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'utils/i18n';

// Import extracted logic
import { useAboutUsCanvas } from './logic/ui';
import translations from './translations';

// Import extracted components from @core
import { AboutUsWavyCanvas, AboutUsVisionSection, AboutUsTeamMember, WorldMap } from 'coreIndex';
import { getApiEndpoint } from 'utils/centralizedConfig';
import { PrimaryButton } from 'feSharedIndex';
import AboutUsSlatMark from './AboutUsSlatMark';
import './AboutUs.css';

/**
 * AboutUs Component
 * Displays information about B-esper company
 */
const AboutUs = () => {
  const { language } = useTranslation();
  const headerRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Use extracted hook for canvas animation
  const { titleRef, subtitleRef } = useAboutUsCanvas(false);
  
  // Get page translations
  const pageTranslations = translations[language] || translations.en;
  
  // Get translations helper
  const t = (key, defaultValue = '') => {
    const value = key.split('.').reduce((obj, k) => obj?.[k], pageTranslations);
    return value || defaultValue || key;
  };

  // Handle PDF download
  const handleDownloadPDF = useCallback(async () => {
    setIsDownloading(true);
    setDownloadError(null);
    
    try {
      // Get API endpoint from centralized config
      const baseUrl = await getApiEndpoint('customer-portal');
      // Construct URL: baseUrl already has /api for cloud, so we only add /api for localhost
      // Route definition is: api/v1/presentation/pdf/{document_type}
      const isLocalhost = baseUrl && (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1'));
      const endpointPath = isLocalhost 
        ? '/api/v1/presentation/pdf/about-us'  // Localhost: baseUrl doesn't have /api
        : '/v1/presentation/pdf/about-us';     // Cloud: baseUrl already has /api
      const apiUrl = `${baseUrl}${endpointPath}?language=${language}`;
      
      // Make request with translations as POST body
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          translations: pageTranslations
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to download PDF: ${response.statusText}`);
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `B-esper_About_Us_${language}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setDownloadError(error.message);
    } finally {
      setIsDownloading(false);
    }
  }, [language, pageTranslations]);

  // Team member data
  const teamMembers = [
    {
      name: t('team.members.wolfgang.name', 'Wolfgang Sperger'),
      role: t('team.members.wolfgang.role', 'CEO & Founder'),
      image: '/resources/images/founders/WS.svg',
      email: 'wolfgang@b-esper.com',
      linkedin: 'https://linkedin.com/in/wolfgang-sperger',
      details: [
        { label: t('team.members.wolfgang.details.experience', 'Experience:'), value: t('team.members.wolfgang.details.experienceValue', 'Has been working in IT architecture for 4 years') },
        { label: t('team.members.wolfgang.details.passion', 'Passion for AI:'), value: t('team.members.wolfgang.details.passionValue', 'Driving innovation in artificial intelligence solutions') },
        { label: t('team.members.wolfgang.details.innovation', 'Innovation Driver:'), value: t('team.members.wolfgang.details.innovationValue', 'Committed to making AI accessible for businesses') }
      ]
    },
    {
      name: t('team.members.sebastian.name', 'Sebastian Höhn'),
      role: t('team.members.sebastian.role', 'Sales & Business Development'),
      image: '/resources/images/founders/SH.svg',
      email: 'sebastian@b-esper.com',
      linkedin: 'https://linkedin.com/in/sebastian-höhn',
      details: [
        { label: t('team.members.sebastian.details.experience', 'Experience:'), value: t('team.members.sebastian.details.experienceValue', 'Nearly 10 years as an IT Consultant and Business Analyst in the Microsoft enviroment and management consulting.') },
        { label: t('team.members.sebastian.details.focus', 'Focus:'), value: t('team.members.sebastian.details.focusValue', 'Specializes in requirements engineering and business analysis to develop tailored solutions for clients.') },
        { label: t('team.members.sebastian.details.sales', 'Sales Expertise:'), value: t('team.members.sebastian.details.salesValue', 'Your contact for all sales activities, with a deep understanding of customer needs and market demands.') }
      ]
    },
    {
      name: t('team.members.cto.name', 'Konstantin'),
      role: t('team.members.cto.role', 'Chief Technology Officer'),
      image: '/resources/images/founders/KA.svg',
      email: '', // Disabled - no email
      linkedin: '', // Disabled - no LinkedIn
      details: [
        { label: '', value: t('team.members.cto.details.comingSoon', 'More information about our CTO will be added soon.') }
      ]
    }
  ];

  return (
    <div className="static-page about-us-page">
      <div className="static-page-header about-us-header" ref={headerRef}>
        <AboutUsWavyCanvas />
        <div className="static-page-header-content">
          <div className="page-title-section">
            <AboutUsSlatMark label={t('slatMarkAria', 'B-esper symbol formed by horizontal slats')} />
            <h1 ref={titleRef} className="animated-title">{t('title', 'We are B-esper')}</h1>
            <p className="lead animated-subtitle" ref={subtitleRef}>
              {t('subtitle', 'Empowering businesses through innovative AI solutions')}
            </p>
            <div className="about-us-actions">
              <PrimaryButton
                className={`about-us-download-btn ${isDownloading ? 'downloading' : ''}`}
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                title={t('getHandout', 'Get our company handout')}
              >
                {isDownloading ? (
                  <>
                    <span className="download-spinner" />
                    <span>{t('downloading', 'Downloading...')}</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>{t('getHandout', 'Get Our Handout')}</span>
                  </>
                )}
              </PrimaryButton>
              {downloadError && (
                <p className="about-us-download-error">{downloadError}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="static-page-content about-us-content-visible">
        <AboutUsVisionSection pageTranslations={pageTranslations} />

        <section className="content-section">
          <h2>{t('whatBesperDoes.heading', 'What B-esper does')}</h2>
          <p>
            {t('whatBesperDoes.paragraph1', 'Besper is an infrastructure company offering the infrastructure and platform to resell products. Our currently only product is Besper Bot, an AI chatbot that can be implemented in many places.')}
          </p>
          <p>
            {t('whatBesperDoes.paragraph2', 'We offer chatbot solutions and a platform where products can be resold and managed by partners for their customers. More products and a marketplace are planned, on which third-party products can be hosted and sold.')}
          </p>
          <WorldMap />
          <div className="vision-grid">
            <div className="vision-card">
              <h3>{t('whatBesperDoes.cloud.heading', 'Cloud & Infrastructure')}</h3>
              <p>
                {t('whatBesperDoes.cloud.description', 'Europe-first infrastructure strategy. We host on Azure in Germany, ensuring data sovereignty and compliance with European regulations.')}
                <br /><br />
                {t('whatBesperDoes.cloud.description2', "You'll be able to select where to host your data and compute. Migration to Kubernetes is planned for first-party hosted products on the platform.")}
              </p>
            </div>
            <div className="vision-card">
              <h3>{t('whatBesperDoes.ai.heading', 'Besper Bot')}</h3>
              <p>
                {t('whatBesperDoes.ai.description', 'Our first in-house product. Managed via API and UI, easily implemented on your website.')}
                <br /><br />
                {t('whatBesperDoes.ai.description2', 'Connect it as your knowledge base with daily updates. Upload your own files. Easy website connection, simple implementation, and self-managing capabilities.')}
              </p>
            </div>
            <div className="vision-card">
              <h3>{t('whatBesperDoes.integration.heading', 'Platform & Infrastructure')}</h3>
              <p>
                {t('whatBesperDoes.integration.description', 'Infrastructure and platform enabling partners to resell products and manage customers.')}
                <br /><br />
                {t('whatBesperDoes.integration.description2', 'The Besper marketplace allows third parties to sell SaaS products, while agencies offer implementation and management services.')}
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>{t('team.heading', 'Meet Our Team')}</h2>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <AboutUsTeamMember key={index} member={member} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
